import { useLocation } from '@gatsbyjs/reach-router'
import { SanityImageSource } from '@sanity/image-url/lib/types/types'
import React, { useContext, useMemo } from 'react'
import { Helmet, MetaProps } from 'react-helmet-async'
import { ThemeContext } from 'styled-components'

import { Maybe } from '../graphql/gatsby'
import useSiteMetadata from '../hooks/useSiteMetadata'
import useSiteSettings from '../hooks/useSiteSettings'
import { imageUrlFor } from '../utils/image-url'

import safariPinnedTab from '../img/safari-pinned-tab.svg'

export interface HeadProps {
  description?: Maybe<string>
  lang?: string
  meta?: MetaProps[]
  title?: Maybe<string>
  keywords?: Maybe<Maybe<string>[]>
  image?: Maybe<SanityImageSource>
}

// See this URL to get a feel for the 'Gatsby style' of adding metadata and anything else to the
// head tag: https://www.gatsbyjs.org/docs/add-page-metadata/
const Head: React.FC<HeadProps> = ({
  description,
  lang = 'en',
  meta = [],
  title,
  keywords = [],
  image = undefined,
}) => {
  const theme = useContext(ThemeContext)
  const location = useLocation()

  const siteMetadata = useSiteMetadata()
  const siteSettings = useSiteSettings()
  const metaDescription =
    description || siteSettings?.description || siteMetadata?.description || ''
  const siteTitle = siteSettings?.title || siteMetadata?.title || ''
  const siteAuthor = siteMetadata?.author || ''
  const metaImage = image ? imageUrlFor(image).width(1200).url() || '' : ''
  const ogUrl = useMemo(() => {
    if (!location) {
      return
    }
    return location.pathname
  }, [location])

  return (
    <Helmet
      htmlAttributes={{ lang }}
      title={title || 'Designs for Health'}
      titleTemplate={title === siteTitle ? '%s' : `%s | ${siteTitle}`}
      meta={(
        [
          {
            charSet: 'utf-8',
          },
          {
            name: 'description',
            content: metaDescription,
          },
          {
            name: 'viewport',
            content: 'width=device-width, initial-scale=1',
          },
          {
            name: 'format-detection',
            content: 'telephone=no',
          },
          {
            name: 'robots',
            content: 'index, follow',
          },
          {
            httpEquiv: 'X-UA-Compatible',
            content: 'IE=edge',
          },
          {
            name: 'apple-mobile-web-app-title',
            content: 'Designs for Health',
          },
          {
            name: 'application-name',
            content: 'Designs for Health',
          },
          {
            name: 'msapplication-TileColor',
            content: theme.colors.brand.orange,
          },
          {
            name: 'theme-color',
            content: theme.colors.brand.white,
          },
          {
            name: 'p:domain_verify',
            content: 'fa5e62b4d0a1d72e1422d009bbcc8616',
          },
          {
            property: 'og:type',
            content: 'business.business',
          },
          {
            property: 'og:title',
            content: title,
          },
          {
            name: 'og:description',
            content: metaDescription,
          },
          {
            property: 'og:url',
            content: ogUrl ?? '/',
          },
          {
            property: 'og:image',
            content: metaImage,
          },
          {
            name: `twitter:card`,
            content: `summary`,
          },
          {
            name: `twitter:creator`,
            content: siteAuthor,
          },
          {
            name: `twitter:title`,
            content: title,
          },
          {
            name: `twitter:description`,
            content: metaDescription,
          },
        ] as MetaProps[]
      )
        .concat(
          keywords && keywords.length
            ? {
                name: 'keywords',
                content: keywords.join(', '),
              }
            : [],
        )
        .concat(meta)}
      link={[{ rel: 'mask-icon', href: safariPinnedTab, color: theme.colors.brand.orange }]}
    />
  )
}

export default Head
